/**
 * Memory, in two layers that look the same. What every agent knows: who you are (always read) and
 * topics — a strategy, a plan, a subject — read only when the conversation is about them, so no one
 * strategy colours everything. And what one agent knows by itself. Each is a row: a name, what it
 * says in one line, when it last changed; press it to read all of it, change it, or forget it.
 */
import { useEffect, useState } from 'react'
import type { Note } from './data'
import { Btn, Dialog, Field, Switch, cx, inputCls } from './kit'
import { useDemo } from './store'

/** A memory open to be read and changed. */
export function MemoryDialog({ open, title, name, text, topic, off, onClose, onSave, onForget }: { open: boolean; title: string; name?: string; text: string; topic?: boolean; off?: boolean; onClose: () => void; onSave: (v: { name: string; text: string; off: boolean }) => void; onForget?: () => void }) {
  const [n, setN] = useState(name ?? '')
  const [t, setT] = useState(text)
  const [o, setO] = useState(!!off)
  useEffect(() => {
    if (open) (setN(name ?? ''), setT(text), setO(!!off))
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={title}
      width={560}
      footer={
        <>
          {onForget && (
            <Btn variant="danger" onPanel className="mr-auto" onClick={() => (onForget(), onClose())}>
              Forget
            </Btn>
          )}
          <Btn variant="outline" onPanel onClick={onClose}>
            Cancel
          </Btn>
          <Btn variant="white" disabled={!t.trim() || (name != null && !n.trim())} onClick={() => (onSave({ name: n.trim(), text: t.trim(), off: o }), onClose())}>
            Save
          </Btn>
        </>
      }
    >
      <div className="space-y-4">
        {name != null && (
          <Field label="Name">
            <input value={n} onChange={(e) => setN(e.target.value)} className={inputCls} />
          </Field>
        )}
        <Field label="What is remembered">
          <textarea value={t} onChange={(e) => setT(e.target.value)} rows={8} className={cx(inputCls, 'h-auto resize-none py-2 leading-[21px]')} />
        </Field>
        {topic && (
          <div className="flex items-center gap-3 rounded-[8px] border border-ws-line px-3 py-2">
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] tracking-normal text-ws-fg">Agents read it</span>
              <span className="block text-[12px] leading-4 tracking-normal text-ws-fg-3">Only when the conversation is about this topic</span>
            </span>
            <Switch on={!o} onChange={(v) => setO(!v)} label="Agents read it" />
          </div>
        )}
      </div>
    </Dialog>
  )
}

const SECTIONS: { id: Note['section']; hint: string }[] = [
  { id: 'You', hint: 'Always read' },
  { id: 'Topics', hint: 'Read when the conversation is about them' },
]

/** What every agent knows — the Memory section of the settings. */
export function SharedMemory() {
  const shared = useDemo((s) => s.shared)
  const save = useDemo((s) => s.saveNote)
  const remove = useDemo((s) => s.removeNote)
  const [openId, setOpenId] = useState<string | null>(null)
  const [fresh, setFresh] = useState(false)
  const [tell, setTell] = useState('')
  const open = shared.find((n) => n.id === openId)
  return (
    <>
      <p className="m-0 text-[12px] leading-[18px] tracking-normal text-ws-fg-3">
        What every agent knows. An agent writes here only what you ask it to keep, or a lasting fact about you; each agent also has a memory of its own, on its page. A temporary chat never writes anything.
      </p>

      {SECTIONS.map((sec) => {
        const list = shared.filter((n) => n.section === sec.id)
        if (!list.length && sec.id !== 'Topics') return null
        return (
          <section key={sec.id}>
            <div className="mb-[6px] flex h-4 items-center justify-between text-[12px] tracking-normal">
              <h2 className="m-0 text-[12px] font-normal leading-4 text-ws-label">{sec.id}</h2>
              {sec.id === 'Topics' ? (
                <button type="button" onClick={() => setFresh(true)} className="text-ws-fg-3 transition-colors hover:text-ws-fg">
                  New topic
                </button>
              ) : (
                <span className="text-ws-fg-4">{sec.hint}</span>
              )}
            </div>
            <div className="divide-y divide-ws-hair overflow-hidden rounded-[8px] border border-ws-line">
              {list.map((n) => (
                <button key={n.id} type="button" onClick={() => setOpenId(n.id)} className="grid min-h-10 w-full grid-cols-[1fr] items-center gap-x-3 px-3 py-[7px] text-left text-[13px] tracking-normal transition-colors duration-150 hover:bg-ws-hover sm:grid-cols-[150px_minmax(0,1fr)_auto]">
                  <span className={cx('truncate', n.off ? 'text-ws-fg-3' : 'text-ws-fg')}>{n.name}</span>
                  <span className="truncate text-ws-fg-3">{n.text.split('\n')[0]}</span>
                  <span className="hidden text-[12px] text-ws-fg-4 sm:block">{n.off ? 'Put aside' : `Updated ${n.updated}`}</span>
                </button>
              ))}
            </div>
          </section>
        )
      })}

      <form
        className="flex h-9 items-center rounded-[8px] border border-ws-line bg-ws-field pl-[10px] pr-1 transition-colors duration-150 focus-within:border-ws-focus"
        onSubmit={(e) => {
          e.preventDefault()
          const t = tell.trim()
          if (!t) return
          save({ id: `new-${Date.now()}`, section: 'You', name: t.split(/\s+/).slice(0, 5).join(' ').replace(/[.,;:!?]+$/, '').slice(0, 48), text: t, updated: 'just now', kind: 'preference' })
          setTell('')
          useDemo.getState().say('Kept — every agent reads it')
        }}
      >
        <input value={tell} onChange={(e) => setTell(e.target.value)} placeholder="Tell every agent something" className="h-full min-w-0 flex-1 tracking-normal" aria-label="Tell every agent something" />
        <button type="submit" disabled={!tell.trim()} className="flex h-7 shrink-0 items-center rounded-[6px] px-2 text-[12px] tracking-normal text-ws-fg-2 transition-colors hover:bg-ws-raise hover:text-ws-fg disabled:text-ws-fg-5 disabled:hover:bg-transparent">
          Remember
        </button>
      </form>

      <MemoryDialog open={!!open} title={open?.section === 'Topics' ? 'Topic' : (open?.section ?? '')} name={open?.name} text={open?.text ?? ''} topic={open?.section === 'Topics'} off={open?.off} onClose={() => setOpenId(null)} onSave={(v) => open && save({ ...open, ...v, updated: 'just now' })} onForget={open ? () => remove(open.id) : undefined} />
      <MemoryDialog open={fresh} title="New topic" name="" text="" topic onClose={() => setFresh(false)} onSave={(v) => save({ id: `n-${Date.now()}`, section: 'Topics', updated: 'just now', ...v })} />
    </>
  )
}
